import pbfe from "./pbfe.js";
import { _, t } from "./i18n.js";
import createNavbar from "./navbar.js";
import { LANG_URL_MAP, getLang, navigate } from "./index.js";

var container, box;

function init(_container) {
    container = _container;
    
    box = new pbfe.Flexbox("column");
    box.element.id = "changelogScreen";
    box.element.className = "main-screen";
    
    // Navbar
    var navbar = createNavbar();
    box.appendChild(navbar);

    // Content Container
    var contentBox = new pbfe.Flexbox("column");
    contentBox.element.id = "changelogContentBox";
    contentBox.alignItems = "center";
    
    // Breadcrumb Container
    var breadcrumbContainer = document.createElement("div");
    breadcrumbContainer.className = "breadcrumb-container";
    
    // Breadcrumb
    var breadcrumb = document.createElement("div");
    breadcrumb.className = "breadcrumb";
    breadcrumbContainer.appendChild(breadcrumb);
    
    // Home Link
    var homeLink = document.createElement("a");
    homeLink.className = "breadcrumb-link";
    homeLink.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg> <span class="breadcrumb-home-text"></span>`;
    t(homeLink.querySelector(".breadcrumb-home-text"), "Home");
    homeLink.addEventListener("click", () => {
        var lang = getLang();
        var prefix = LANG_URL_MAP[lang];
        var path = prefix ? "/" + prefix : "/";
        
        navigate(path);
    });
    breadcrumb.appendChild(homeLink);
    
    // Separator
    var separator = document.createElement("span");
    separator.className = "breadcrumb-separator";
    separator.innerHTML = `<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>`;
    breadcrumb.appendChild(separator);
    
    // Current Page
    var current = document.createElement("span");
    current.className = "breadcrumb-current";
    current.innerText = "";
    t(current, "Changelog");
    breadcrumb.appendChild(current);
    
    contentBox.appendChild({ element: breadcrumbContainer });
    
    // Title
    var title = document.createElement("h1");
    title.className = "page-title";
    title.innerText = "";
    t(title, "Changelog");
    contentBox.appendChild({ element: title });

    // Changelog List Container
    var listContainer = document.createElement("div");
    listContainer.className = "changelog-container";

    var releases = [
        {
            version: "v1.2.5",
            date: _("December 13th, 2025"),
            highlight: {
                icon: "📝",
                title: _("Blog & Content Update"),
                desc: _("Added a new Blog section, optimized blog post experience, and improved site navigation.")
            },
            changes: [
                { type: "new", text: _("Added a new Blog section and blog list page.") },
                { type: "new", text: _("Added new blog post: 'How do I use avatar cropper to crop avatar from an image?'.") },
                { type: "new", text: _("Added 404 error page.") },
                { type: "improve", text: _("Optimized blog post page with internal links and clickable titles.") },
                { type: "improve", text: _("Updated 'Read Article' button style for better consistency.") },
            ]
        },
        {
            version: "v1.2.0",
            date: _("December 7th, 2025"),
            highlight: {
                icon: "🎨",
                title: _("Major UI/UX Overhaul"),
                desc: _("A complete redesign of the homepage, navigation, and page structure for a more modern and stable experience.")
            },
            changes: [
                { type: "new", text: _("Split Welcome and Editor pages with dedicated URLs for better navigation.") },
                { type: "new", text: _("Redesigned Homepage with a stunning dark theme and consistent UI elements.") },
                { type: "new", text: _("Modernized Navigation Bar with glassmorphism design and centered layout.") },
                { type: "new", text: _("Moved Changelog to a standalone page accessible via the new Navbar.") },
                { type: "fix", text: _("Resolved 'Loading...' stuck issue and improved page transition stability.") }
            ]
        },
        {
            version: "v1.1.0",
            date: _("December 4th, 2025"),
            highlight: {
                icon: "🚀",
                title: _("New Features & Improvements"),
                desc: _("Import images directly from URLs and enjoy a fully localized experience in all supported languages.")
            },
            changes: [
                { type: "new", text: _("Added 'From A Image URL' feature to import images via URL.") },
                { type: "improve", text: _("Completed translations for all supported languages.") },
                { type: "improve", text: _("Enhanced 'Open Image From URL' dialog UI.") },
                { type: "fix", text: _("Fixed deployment compatibility issues.") }
            ]
        },
        {
            version: "v1.0.0",
            date: _("November 30th, 2025"),
            highlight: {
                icon: "🎉",
                title: _("Official Release"),
                desc: _("We are thrilled to announce the first official release of Avatar Cropper! Crop, edit, and perfect your avatars with ease.")
            },
            changes: [
                { type: "new", text: _("Brand new About & Privacy pages with modern glassmorphism design.") },
                { type: "new", text: _("Enhanced Language Selector with flag support.") },
                { type: "improve", text: _("Optimized image processing for faster cropping.") },
                { type: "improve", text: _("Improved UI responsiveness on mobile devices.") },
                { type: "fix", text: _("Fixed minor layout issues in dark mode.") }
            ]
        },
        {
            version: "v0.9.0",
            date: _("November 15th, 2025"),
            changes: [
                { type: "new", text: _("Initial beta release.") },
                { type: "new", text: _("Basic cropping functionality (Circle & Square).") },
                { type: "new", text: _("Dark mode support.") }
            ]
        }
    ];

    releases.forEach((release, index) => {
        var wrapper = document.createElement("div");
        wrapper.className = "changelog-card";
        
        // Header
        var header = document.createElement("div");
        header.className = "changelog-header";
        
        var version = document.createElement("span");
        version.className = "changelog-version";
        version.innerText = release.version;
        
        var date = document.createElement("span");
        date.className = "changelog-date";
        date.innerText = release.date;
        
        header.appendChild(version);
        header.appendChild(date);
        wrapper.appendChild(header);

        // Highlight
        if (release.highlight) {
            var highlight = document.createElement("div");
            highlight.className = "changelog-highlight";
            highlight.innerHTML = `
                <div class="highlight-icon">${release.highlight.icon}</div>
                <div class="highlight-text">
                    <h3>${release.highlight.title}</h3>
                    <p>${release.highlight.desc}</p>
                </div>
            `;
            wrapper.appendChild(highlight);
        }

        // List
        var ul = document.createElement("ul");
        ul.className = "changelog-list";
        
        if (release.changes) {
            release.changes.forEach(item => {
                var li = document.createElement("li");
                li.className = `changelog-item ${item.type}`;
                var icon = item.type === "new" ? "✨" : (item.type === "improve" ? "⚡" : "🐛");
                li.innerHTML = `<span class="item-icon">${icon}</span> <span class="item-text">${item.text}</span>`;
                ul.appendChild(li);
            });
        }
        
        wrapper.appendChild(ul);
        listContainer.appendChild(wrapper);
    });

    contentBox.appendChild({ element: listContainer });
    box.appendChild(contentBox);
}

function show() {
    container.appendChild(box);
    // Trigger reflow or simple timeout to allow animation if we add one later
    setTimeout(() => box.element.classList.add("show"), 0);
}

function hide() {
    if (!box) return;
    box.element.classList.remove("show");
    if (container && container.contains(box)) {
        container.removeChild(box);
    }
}

var changelogScreen = {
    init, show, hide
};

export default changelogScreen;
